import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
// import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type {
  IColumnsMap,
  IFilterMap,
  IFiltersMap,
} from 'libs/design-system/src/types/data_table';
import { DataTable } from '@unified-checkout/design-system';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import { INNER_ROUTES } from '@app/dashboard/constants/inner-routes';
import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import type { IOrdersDateRange } from '@app/dashboard/types/innerLayout/orders';
import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';
import { useLangStore } from '@app/dashboard/store/lang';
// import { getOrdersFilters } from '@app/dashboard/services/inner_layout/orders/orders-filters';
import { getOrders } from '@app/dashboard/services/inner_layout/orders';
import { getCountryFromDomain } from '@app/dashboard/utils/getCountryFromDomain';
import { Header } from './Header';
import { getOrderStatus } from './utils';
import styles from './orders.module.scss';

const TABLE_ID = 'orders_table';

export const Orders = () => {
  const { t } = useTranslation();
  const screenWidth = useScreenWidth();
  const navigate = useNavigate();

  const { lang } = useLangStore();
  const { isLive } = useTestLiveModeStore();

  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const [isError, setIsError] = useState(false);
  const [dateRange, setDateRange] = useState<IOrdersDateRange>({
    date_from: '',
    date_to: '',
  });

  const columnsMap: IColumnsMap = [
    {
      id: 'order_id',
      name: t('innerLayout.ordersPage.table.headers.orderId'),
      type: 'text',
      key: 'id',
    },
    {
      id: 'date',
      name: t('innerLayout.ordersPage.table.headers.dateCreated'),
      type: 'date',
      key: 'created_at',
      hintText: 'created_at',
    },
    {
      id: 'amount',
      name: t('innerLayout.ordersPage.table.headers.amount'),
      type: 'amount_cents',
      key: 'amount_cents',
      additionalText: {
        key: 'currency',
        placement: lang === 'en' ? 'start' : 'end',
      },
    },
    {
      id: 'merchant_order_id',
      name: t('innerLayout.ordersPage.table.headers.merchantOrderId'),
      type: 'text',
      key: 'merchant_order_id',
    },
    {
      id: 'client_name',
      name: t('innerLayout.ordersPage.table.headers.clientName'),
      type: 'text',
      key: 'shipping_data',
      format: (fullData: any, value: any) => {
        return value ? `${value.first_name} ${value.last_name}` : "";
      }
    },
    {
      id: 'phone',
      name: t('innerLayout.ordersPage.table.headers.phone'),
      type: 'text',
      key: 'shipping_data.phone_number',
    },
    {
      id: 'cart',
      name: t('innerLayout.ordersPage.table.headers.cart'),
      type: 'text',
      key: 'items',
      format: (fullData: any, value: any) => {
        return Array.isArray(value) && value.length > 0 ? `${value[0].name}` : "";
      }
    },
    {
      id: 'status',
      name: t('innerLayout.ordersPage.table.headers.status'),
      type: 'badge',
      key: 'payment_status',
      format: (fullData: any, value: any) => getOrderStatus(value),
      labelComparingValue: 'payment_status',
      label: {
        [`PAID`]: { color: 'green' },
        [`UNPAID`]: { color: 'yellow' },
        [`CANCELED`]: { color: 'gray' },
      },
      labelFallback: { color: "yellow" }
    },
  ];

  const filtersResponse = [
    {
      filter_name: t('innerLayout.ordersPage.table.filters.currency'),
      filter_key: 'currency',
      filter_values: [
        {
          [t('innerLayout.ordersPage.table.filters.options.currency.EGP')]: 'EGP',
        },
        {
          [t('innerLayout.ordersPage.table.filters.options.currency.AED')]: 'AED',
        },
        {
          [t('innerLayout.ordersPage.table.filters.options.currency.SAR')]: 'SAR',
        },
        {
          [t('innerLayout.ordersPage.table.filters.options.currency.USD')]: 'USD',
        },
        {
          [t('innerLayout.ordersPage.table.filters.options.currency.OMR')]: 'OMR',
        },
        {
          [t('innerLayout.ordersPage.table.filters.options.currency.BHD')]: 'BHD',
        },
      ],
      filter_type: 'dropdown',
      default_selection: 'all',
    },
    {
      filter_name: t('innerLayout.ordersPage.table.filters.orderId'),
      filter_key: 'order_id',
      filter_type: 'text_box',
      filter_place_holder: '1234',
      filter_validation: [
        {
          regex: '^\\d{1,15}$',
          message: t('innerLayout.ordersPage.table.filters.validation.orderIdMessage'),
        },
      ]
    },
    {
      filter_name: t('innerLayout.ordersPage.table.filters.merchantOrderId'),
      filter_key: 'merchant_order_id',
      filter_type: 'text_box',
      filter_place_holder: '1234',
      filter_validation: [
        {
          regex: '^[a-zA-Z0-9\\s_\\-@#&./(),]+$',
          message: t('innerLayout.ordersPage.table.filters.validation.merchantOrderIdMessage'),
        },
      ],
    },
    {
      filter_name: t('innerLayout.ordersPage.table.filters.status'),
      filter_key: 'status',
      filter_values: [
        {
          [t('innerLayout.ordersPage.table.filters.options.status.paid')]: 'paid',
        },
        {
          [t('innerLayout.ordersPage.table.filters.options.status.unpaid')]: 'unpaid',
        },
      ],
      filter_type: 'dropdown',
      default_selection: 'all',
    },
    // {
    //   filter_name: t('innerLayout.ordersPage.table.filters.amountFrom'),
    //   filter_key: 'amount_from',
    //   filter_type: 'text_box',
    //   filter_place_holder: '1234',
    //   filter_validation: [
    //     {
    //       regex: '^\\d{1,15}$',
    //       message: t('innerLayout.ordersPage.table.filters.validation.amountMessage'),
    //     },
    //   ],
    // },
    // {
    //   filter_name: t('innerLayout.ordersPage.table.filters.amountTo'),
    //   filter_key: 'amount_to',
    //   filter_type: 'text_box',
    //   filter_place_holder: '1234',
    //   filter_validation: [
    //     {
    //       regex: '^\\d{1,15}$',
    //       message: t('innerLayout.ordersPage.table.filters.validation.amountMessage'),
    //     },
    //   ],
    // },
    // {
    //   filter_name: t('innerLayout.ordersPage.table.filters.paidAmountFrom'),
    //   filter_key: 'paid_amount_from',
    //   filter_type: 'text_box',
    //   filter_place_holder: '1234',
    //   filter_validation: [
    //     {
    //       regex: '^\\d{1,15}$',
    //       message: t('innerLayout.ordersPage.table.filters.validation.amountMessage'),
    //     },
    //   ],
    // },
    // {
    //   filter_name: t('innerLayout.ordersPage.table.filters.paidAmountTo'),
    //   filter_key: 'paid_amount_to',
    //   filter_type: 'text_box',
    //   filter_place_holder: '1234',
    //   filter_validation: [
    //     {
    //       regex: '^\\d{1,15}$',
    //       message: t('innerLayout.ordersPage.table.filters.validation.amountMessage'),
    //     },
    //   ],
    // },
    {
      filter_name: t('innerLayout.ordersPage.table.filters.orderUrl'),
      filter_key: 'order_url',
      filter_type: 'text_box',
      filter_validation: [
        {
          regex: '^https?:\\/\\/(?:www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b(?:[-a-zA-Z0-9()@:%_\\+.~#?&\\/=]*)$',
          message: t('innerLayout.ordersPage.table.filters.validation.orderUrlMessage'),
        },
      ],
    },
    {
      filter_name: t('innerLayout.ordersPage.table.filters.createdBy'),
      filter_key: 'merchant_staff_tag',
      filter_type: 'text_box',
      filter_validation: [
        {
          //field should accept either email, phone, or username
          regex: '^([a-zA-Z0-9._\\- ]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}|[0-9 ]+|[a-zA-Z0-9._\\- ]+)$',
          message: t('innerLayout.ordersPage.table.filters.validation.createdByMessage'),
        },
      ],
      filter_place_holder: t('innerLayout.ordersPage.table.filters.validation.createdByMessagePlaceholder')
    },
    {
      filter_name: t('innerLayout.ordersPage.table.filters.phoneNumber'),
      filter_key: 'phone_number',
      filter_type: 'phone_number',
      filter_place_holder: '01099948835',
      filter_validation: [
        {
          regex: '^\\+?\\d{9,15}$',
          message: t('innerLayout.ordersPage.table.filters.validation.phoneNumberMessage'),
        },
      ],
    },
    {
      filter_name: t('innerLayout.ordersPage.table.filters.lastFourDigit'),
      filter_key: 'card_last_digits',
      filter_type: 'text_box',
      filter_place_holder: '1234',
      filter_validation: [
        {
          regex: '^\\d{1,4}$',
          message: t('innerLayout.ordersPage.table.filters.validation.lastFourDigitMessage'),
        },
      ],
    },
    {
      filter_name: t('innerLayout.ordersPage.table.filters.deliveryNeeded'),
      filter_key: 'delivery_needed',
      filter_values: [
        {
          [t('innerLayout.ordersPage.table.filters.options.deliveryNeeded.yes')] : true,
        },
        {
          [t('innerLayout.ordersPage.table.filters.options.deliveryNeeded.no')] : false,
        },
      ],
      filter_type: 'dropdown',
      default_selection: 'all',
    },
    {
      filter_name: t('innerLayout.ordersPage.table.filters.splitPayments'),
      filter_key: 'is_split_paid',
      filter_values: [
        {
          [t('innerLayout.ordersPage.table.filters.options.splitPayments.yes')] : true,
        },
        {
          [t('innerLayout.ordersPage.table.filters.options.splitPayments.no')] : false,
        },
      ],
      filter_type: 'dropdown',
      default_selection: 'all',
    }
  ];

  // const {
  //   isLoading: isFiltersLoading,
  //   isError: isFiltersError,
  //   data: filtersResponse
  // } = useQuery({
  //   queryKey: [QUERY_KEYS.GET_ORDERS_FILTERS, isLive, lang],
  //   queryFn: () => getOrdersFilters({ isLive })
  // });

  const filtersMap: IFiltersMap = useMemo(() => {
    return filtersResponse.map((filter: any) => {
      const {
        filter_type,
        filter_key,
        filter_name,
        filter_values,
        default_selection,
        filter_place_holder,
        filter_validation
      } = filter;

      const filterType: Record<string, IFilterMap['type']> = {
        dropdown: 'select',
        multi_selection_dropdown: 'multi_selection',
        text_box: 'text',
        phone_number: 'phone',
      };

      return {
        type: filterType[filter_type],
        key: filter_key,
        name: filter_name,
        values: filter_values,
        defaultSelection: default_selection,
        placeHolder: filter_place_holder,
        validations: filter_validation,
      };
    });
  }, [filtersResponse]);

  const handleTableRowClick = (rowObj: any) => {
    rowObj && navigate(INNER_ROUTES.SINGLE_ORDER.replace(':id', rowObj.id));
  };

  const queryKeys = useMemo(
    () => [QUERY_KEYS.GET_ORDERS, isLive, dateRange],
    [isLive, dateRange]
  );

  const queryFunctionParams = useMemo(
    () => ({
      ...dateRange,
      is_live: isLive,
    }),
    [dateRange, isLive]
  );

  const resetFiltersParams = useMemo(() => [isLive], [isLive]);

  return (
    <div className={styles['orders-wrapper']}>
      <Header
        setDateRange={setDateRange}
      />

      <DataTable
        lang={lang}
        filters={{
          filtersMap: filtersMap,
          // isLoading: false,
          // isError: false,
          isMobile: isMobile,
          country: getCountryFromDomain(),
        }}
        tableBody={{
          lang: lang,
          tableId: TABLE_ID,
          dataLocation: 'data.results',
          columnsMap: columnsMap,
          rowClickHandler: handleTableRowClick,
        }}
        pagination={{
          hideLabels: isMobile,
          type: 'cursor_based',
          dataLocation: {
            next: 'data.next',
            prev: 'data.previous',
          },
          isMobile: isMobile,
          // maxVisiblePages: 5,
          // compactMode: isMobile
        }}
        queries={{
          queryKeys: queryKeys,
          queryFunction: getOrders,
          queryFunctionParams: queryFunctionParams,
          resetFiltersParams: resetFiltersParams,
        }}
        errorState={{
          onError: (error) => setIsError(error),
        }}
        emptyState={{
          text: t('innerLayout.ordersPage.table.body.noOrdersYet'),
          description: t('innerLayout.ordersPage.table.body.noOrdersHint'),
        }}
      />
    </div>
  );
};
